import { BankOutlined } from "@ant-design/icons"
import { Button, Result } from "antd"
import React from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { ALL_BRANCHES } from "../redux/branches.constants.ts"
import {
  selectCompanyIsMultiBranch,
  selectIsCompanyMode,
  setGlobalBranch
} from "../redux/branches.slice.ts"

/**
 * Wraps an HQ-only management screen (today: Settings -> Branches) so a
 * direct visit while a specific branch workspace is active never renders
 * its company-wide controls under that branch's authority — switching the
 * GLOBAL workspace selector already keeps this screen off the tab bar and
 * redirects away from it (see `BranchSelector`), but a bookmarked/typed
 * URL, a browser Back/Forward, or a stale open tab reach the route
 * directly, bypassing that.
 *
 * A single-operation account (`Gcc.multi_branch_enabled` is explicit-admin-
 * only, never inferred from role or branch count) has no Company HQ to
 * switch to, so this gate is a no-op for it — `BranchesOwnerGate` (the
 * caller) already refuses that case outright before this ever mounts, so
 * this is a second, independent check on the same real flag rather than
 * the only one. Ownership is NOT re-checked here — the caller wraps this
 * inside its own owner-only gate, since an unauthorized visitor must keep
 * seeing "Access Restricted", never this prompt.
 */
export function OpenInCompanyHQGate({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch()
  const isMultiBranch = useAppSelector(selectCompanyIsMultiBranch)
  const isCompanyMode = useAppSelector(selectIsCompanyMode)

  if (!isMultiBranch || isCompanyMode) return <>{children}</>

  return (
    <Result
      icon={<BankOutlined />}
      title="This is managed from Company HQ"
      subTitle="Branch creation, archiving and restoring apply across every branch, so they live in the Company HQ workspace — not inside an individual branch."
      extra={
        <Button
          type="primary"
          data-testid="open-in-company-hq"
          onClick={() => dispatch(setGlobalBranch(ALL_BRANCHES))}
        >
          Open in Company HQ
        </Button>
      }
    />
  )
}

export default OpenInCompanyHQGate
